import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "@/lib/toast";

import apiClient from "@/lib/api-client";
import { useActAsStore } from "@/stores/act-as-store";

/**
 * Start / stop admin "act as" impersonation.
 *
 * `start(user, reason?)` — POST /api/admin/users/{id}/act-as, EVICT all React
 * Query cache entries (synchronous via removeQueries), flip the store to the
 * target, toast, and navigate home.
 *
 * `stop()` — DELETE /api/admin/users/{id}/act-as, EVICT caches, clear store.
 * The `finally` clause still clears the store even if the DELETE fails —
 * a stuck client-side banner is worse than a harmless server-side expiry.
 *
 * Ordering is load-bearing: removeQueries() MUST run BEFORE setActingAs /
 * clearActingAs. React Query's invalidateQueries keeps stale entries in the
 * cache until refetch completes, which means a fast re-render after the
 * store flip could briefly paint the previous identity's data under the new
 * identity. removeQueries evicts synchronously — any subsequent useQuery
 * hook observes an empty cache and must wait for a fresh fetch (which the
 * ky middleware will dispatch with the correct X-Act-As-User state).
 */
export function useActAs() {
  const qc = useQueryClient();
  const setActingAs = useActAsStore((s) => s.setActingAs);
  const clearActingAs = useActAsStore((s) => s.clearActingAs);
  const navigate = useNavigate();

  const start = async (
    user: { id: string; email: string },
    opts: { reason?: string; ttlHours?: 1 | 4 | 12 | 24 } | string = {},
  ) => {
    // Backwards-compat: callers that passed a bare reason string (legacy
    // signature pre-TTL) still work. New callers pass { reason, ttlHours }.
    const normalized =
      typeof opts === "string" ? { reason: opts } : opts;
    const body: { reason?: string; ttl_hours?: number } = {};
    if (normalized.reason) body.reason = normalized.reason;
    if (normalized.ttlHours) body.ttl_hours = normalized.ttlHours;
    const res = await apiClient
      .post(`api/admin/users/${user.id}/act-as`, { json: body })
      .json<{ expires_at: string }>();
    const expiresAt = new Date(res.expires_at).getTime();
    // Evict BEFORE flipping the store so no query resolution can render the
    // caller's cached data under the target's identity.
    qc.removeQueries();
    setActingAs(user.id, user.email, expiresAt);
    toast.success(`Now acting as ${user.email}`);
    navigate({ to: "/" });
  };

  const stop = async () => {
    const userId = useActAsStore.getState().actingAsUserId;
    if (!userId) return;
    try {
      await apiClient.delete(`api/admin/users/${userId}/act-as`);
    } finally {
      // Evict BEFORE clearing the store so target-identity data cannot
      // leak into the admin's own view on the next render.
      qc.removeQueries();
      clearActingAs();
      toast.success("Stopped acting-as");
    }
  };

  return { start, stop };
}
